import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded'
import RefreshIcon from '@mui/icons-material/Refresh'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import FilePreviewDialog from '@/components/ui/dialogs/FilePreviewDialog/FilePreviewDialog'
import Document from '@/components/group/Document'
import UploadButton from '@/components/ui/buttons/UploadButton'
import QrCodeDialog from '@/pages/diagnostic/components/QrCodeDialog'
import breakpoints from '@/assets/themes/breakpoints'

export type PatientDocumentListProps = {
  documents: Array<{
    filename: string
    key: string
    url: string
    isOffline?: boolean
  }>
  page?: number
  totalCount?: number
  isDeleting?: boolean
  isRefreshing?: boolean
  onDelete?: (documents: PatientDocumentListProps['documents']) => void
  onSaveFileName?: (filename: string, key: string) => void
  onSaveFileAnnotation?: (file: File, key: string) => void
  onUpload?: (file: File) => void
  onPageChange?: (page: number) => void
  onRefresh?: () => void
  qrCode?: string
  pageSize?: number
}

export function PatientDocumentList(props: PatientDocumentListProps) {
  const {
    documents,
    totalCount = 0,
    isDeleting,
    isRefreshing,
    onDelete,
    onSaveFileName,
    onSaveFileAnnotation,
    onUpload,
    qrCode,
    page = 1,
    onPageChange,
    onRefresh,
    pageSize = 12,
  } = props
  const { t } = useTranslation()
  const { width: documentsWrapperWidth, ref: documentsWrapperRef } = useResizeDetector()

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false)
  const [selectedDocuments, setSelectedDocuments] = useState<Record<string, PatientDocumentListProps['documents'][number]>>({})

  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState<boolean>(false)
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false)
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number>(-1)

  const pageCount = useMemo(() => Math.max(1, Math.ceil(totalCount / pageSize)), [pageSize, totalCount])

  const documentsGridBreakpoint = useMemo(() => {
    if (!breakpoints.values || !documentsWrapperWidth) return 12
    if (documentsWrapperWidth > breakpoints.values.lg) return 2
    if (documentsWrapperWidth > breakpoints.values.sm) return 3
    if (documentsWrapperWidth > breakpoints.values.mobile) return 4
    return 12
  }, [documentsWrapperWidth])

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    onPageChange?.(value)
  }

  const toggleQrCodeDialog = () => {
    setQrCodeDialogOpen(!qrCodeDialogOpen)
  }

  // reset selectedDocuments after delete
  useEffect(() => {
    if (!!_.keys(selectedDocuments).length && !_.intersectionBy(documents, _.values(selectedDocuments), 'key').length) {
      setSelectedDocuments({})
    }
  }, [documents, selectedDocuments])

  useEffect(() => {
    onPageChange?.(Math.min(page, pageCount))
  }, [pageCount])

  const handleChangeDocumentSelection = (document: PatientDocumentListProps['documents'][number]) => (event: React.ChangeEvent, checked: boolean) => {
    setSelectedDocuments((prevSelectedDocuments) => {
      return !checked ? _.omit(prevSelectedDocuments, document.key) : { ...prevSelectedDocuments, [document.key]: document }
    })
  }

  const handleDelete = () => {
    onDelete?.(_.values(selectedDocuments))
  }

  const toggleDeleteConfirmationDialog = () => {
    setDeleteConfirmationDialogOpen((prevState) => !prevState)
  }

  const handleOpenPreview = (index: number) => {
    setCurrentPreviewIndex(index)
    setPreviewDialogOpen(true)
  }

  const handleClosePreview = () => {
    setPreviewDialogOpen(false)
    setCurrentPreviewIndex(-1)
  }

  const handleNextDocument = () => {
    if (currentPreviewIndex < documents.length - 1) {
      setCurrentPreviewIndex(currentPreviewIndex + 1)
    }
  }

  const handlePreviousDocument = () => {
    if (currentPreviewIndex > 0) {
      setCurrentPreviewIndex(currentPreviewIndex - 1)
    }
  }

  const handleSaveFileNameFromPreview = (filename: string) => {
    if (currentPreviewIndex >= 0) {
      const currentDoc = documents[currentPreviewIndex]
      onSaveFileName?.(filename, currentDoc.key)
    }
  }

  const handleSaveFileAnnotationFromPreview = (file: File) => {
    if (currentPreviewIndex >= 0) {
      const currentDoc = documents[currentPreviewIndex]
      onSaveFileAnnotation?.(file, currentDoc.key)
    }
  }

  return (
    <Box data-testid="patient-document-list" sx={{ position: 'relative' }}>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ position: 'absolute', top: 0, right: 0 }}>
        {onRefresh && (
          <Tooltip title={<span>{t('common:button.refresh.tooltip')}</span>} placement="top">
            <span>
              <Button disabled={isRefreshing} onClick={onRefresh} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                {isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
              </Button>
            </span>
          </Tooltip>
        )}
        {!!_.keys(selectedDocuments).length && (
          <Tooltip
            title={<span>{t('page.infoPatient.documents.deleteDocumentButton.tooltip', { count: _.keys(selectedDocuments).length })}</span>}
            placement="top"
          >
            <span>
              <Button disabled={isDeleting} onClick={toggleDeleteConfirmationDialog} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
                {isDeleting ? <CircularProgress size={18} /> : <DeleteRoundedIcon />}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Tooltip title={<span>{t('page.liveConsultation.documents.showQrcodeButton.tooltip')}</span>} placement="top">
        <span>
          <Button disabled={!qrCode} onClick={toggleQrCodeDialog} size="small" variant="chip" sx={{ minWidth: 'unset' }}>
            <QrCode2RoundedIcon />
          </Button>
        </span>
      </Tooltip>

      <Box ref={documentsWrapperRef} sx={{ py: '15px' }}>
        <Grid container spacing={2}>
          {_.map(documents, (document, index) => {
            return (
              <Grid key={document.key} item xs={documentsGridBreakpoint}>
                <Box sx={{ position: 'relative' }} maxWidth={280} mx="auto">
                  <Document
                    extension={_.split(document.filename, '.')[1]}
                    fileKey={document.key}
                    name={_.split(document.filename, '.')[0]}
                    onSaveFileName={onSaveFileName}
                    onSaveFileAnnotation={onSaveFileAnnotation}
                    size=""
                    src={document.url}
                    isOffline={document.isOffline}
                    onOpenPreview={() => handleOpenPreview(index)}
                  />
                  <Checkbox
                    checked={!!selectedDocuments[document.key]}
                    size="small"
                    sx={{ position: 'absolute', top: '0', right: '0' }}
                    onChange={handleChangeDocumentSelection(document)}
                  />
                </Box>
              </Grid>
            )
          })}
          <Grid item xs={documentsGridBreakpoint}>
            <Box maxWidth={280} mx="auto">
              <UploadButton onUpload={onUpload} />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {pageCount > 1 && (
        <Stack alignItems="center" mt={2} spacing={2}>
          <Pagination count={pageCount} page={page} onChange={handleChangePage} shape="rounded" variant="outlined" />
        </Stack>
      )}
      {qrCode && <QrCodeDialog onClose={toggleQrCodeDialog} open={qrCodeDialogOpen} qrCodeValue={qrCode} />}
      <DeleteConfirmationDialog
        isDeleting={isDeleting}
        onClose={toggleDeleteConfirmationDialog}
        onDelete={handleDelete}
        open={deleteConfirmationDialogOpen}
        title={t('dialog.deleteConfirmation.documents.title', { count: _.keys(selectedDocuments).length })}
        message={t('dialog.deleteConfirmation.documents.message')}
      />
      {currentPreviewIndex >= 0 && documents[currentPreviewIndex] && (
        <FilePreviewDialog
          fileName={_.split(documents[currentPreviewIndex].filename, '.')[0]}
          fileType={_.split(documents[currentPreviewIndex].filename, '.')[1]}
          onClose={handleClosePreview}
          onSaveFileName={handleSaveFileNameFromPreview}
          open={previewDialogOpen}
          PhotoViewerProps={{
            onSave: handleSaveFileAnnotationFromPreview,
          }}
          src={documents[currentPreviewIndex].url}
          onNext={currentPreviewIndex < documents.length - 1 ? handleNextDocument : undefined}
          onPrevious={currentPreviewIndex > 0 ? handlePreviousDocument : undefined}
        />
      )}
    </Box>
  )
}
